import { useEffect, useMemo, useState, type FormEvent } from 'react'
import {
  Plus,
  MagnifyingGlass,
  Buildings,
  PencilSimple,
  Eye,
  EyeSlash,
  CheckCircle,
  XCircle,
  Warning,
  Phone,
  EnvelopeSimple,
  Bank,
  MapPin,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react'
import { Modal } from '@/components/Modal'
import { supabase } from '@/lib/supabase'

/**
 * Catálogo de Proveedores — base para el módulo de Gastos.
 *
 * El campo CLAVE es `regimen_tributario`: define si al cargar una factura
 * de este proveedor el sistema debe retener ISR automáticamente.
 *
 * Según Carlitos (2026-06-26):
 *   - DEFINITIVO              → factura dice "SUJETO A RETENCIÓN DEFINITIVA" → retiene 5%/7%
 *   - PAGOS_TRIMESTRALES      → factura dice "SUJETO A PAGOS TRIMESTRALES"   → NO retiene
 *   - PEQUEÑO_CONTRIBUYENTE   → factura de Pequeño Contribuyente             → NO retiene
 *   - OTROS                   → caso a caso, el operador decide en el form
 *
 * Cuando el operador elige un proveedor en el form de Gastos, ese form
 * pre-selecciona "¿sujeto a retención?" según este régimen.
 */

type Regimen = 'DEFINITIVO' | 'PAGOS_TRIMESTRALES' | 'PEQUEÑO_CONTRIBUYENTE' | 'OTROS'

type Proveedor = {
  id: string
  nit: string | null
  nombre: string
  regimen_tributario: Regimen | null
  email: string | null
  telefono: string | null
  banco: string | null
  cuenta_bancaria: string | null
  direccion: string | null
  notas: string | null
  activo: boolean
  created_at: string
  updated_at: string
}

const REGIMENES: Record<Regimen, { label: string; sub: string; color: string; bg: string; retiene: boolean }> = {
  DEFINITIVO: {
    label: 'Sujeto a Retención Definitiva',
    sub: 'Retiene ISR 5% / 7%',
    color: '#F04E23',
    bg: 'rgba(240,78,35,0.10)',
    retiene: true,
  },
  PAGOS_TRIMESTRALES: {
    label: 'Pagos Trimestrales',
    sub: 'Régimen sobre Utilidades · No retiene',
    color: '#0F8A57',
    bg: 'rgba(15,138,87,0.10)',
    retiene: false,
  },
  PEQUEÑO_CONTRIBUYENTE: {
    label: 'Pequeño Contribuyente',
    sub: 'No retiene',
    color: '#0F8A57',
    bg: 'rgba(15,138,87,0.10)',
    retiene: false,
  },
  OTROS: {
    label: 'Otros / por confirmar',
    sub: 'Operador decide caso a caso',
    color: '#9aa0a6',
    bg: 'rgba(154,160,166,0.12)',
    retiene: false,
  },
}

export function NeoCoreProveedoresPage() {
  const [rows, setRows] = useState<Proveedor[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [filtroActivo, setFiltroActivo] = useState<'todos' | 'activos' | 'inactivos'>('activos')
  const [editing, setEditing] = useState<Proveedor | null>(null)
  const [creating, setCreating] = useState(false)

  const reload = () => {
    setLoading(true)
    setError(null)
    supabase
      .from('proveedor')
      .select('*')
      .order('activo', { ascending: false })
      .order('nombre', { ascending: true })
      .then(({ data, error }) => {
        if (error) setError(error.message)
        else setRows((data ?? []) as Proveedor[])
        setLoading(false)
      })
  }
  useEffect(reload, [])

  // Filtrado en cliente (la lista no debería superar cientos de filas)
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return rows.filter((r) => {
      if (filtroActivo === 'activos' && !r.activo) return false
      if (filtroActivo === 'inactivos' && r.activo) return false
      if (!q) return true
      return [r.nombre, r.nit, r.email, r.telefono, r.banco].some(
        (v) => v && String(v).toLowerCase().includes(q),
      )
    })
  }, [rows, search, filtroActivo])

  const stats = useMemo(() => {
    const total = rows.length
    const activos = rows.filter((r) => r.activo).length
    const conIsr = rows.filter((r) => r.regimen_tributario === 'DEFINITIVO').length
    const sinRegimen = rows.filter((r) => !r.regimen_tributario || r.regimen_tributario === 'OTROS').length
    return { total, activos, conIsr, sinRegimen }
  }, [rows])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* Header con stats inline + acción */}
      <div
        style={{
          display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
          gap: 12, flexWrap: 'wrap',
        }}
      >
        <div className="stats-inline">
          <span className="stat-num">{stats.activos}</span>
          <span className="stat-label">activos</span>
          <span className="stat-sep">·</span>
          <span className="stat-num">{stats.conIsr}</span>
          <span className="stat-label">con retención ISR</span>
          {stats.sinRegimen > 0 && (
            <>
              <span className="stat-sep">·</span>
              <span className="stat-num" style={{ color: 'var(--brand-600)' }}>{stats.sinRegimen}</span>
              <span className="stat-label">sin régimen definido</span>
            </>
          )}
        </div>
        <button
          className="btn btn-primary"
          onClick={() => setCreating(true)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <Plus size={14} weight="bold" />
          Nuevo proveedor
        </button>
      </div>

      {/* Filtros + búsqueda */}
      <div
        className="card"
        style={{
          padding: '12px 14px', display: 'flex', alignItems: 'center',
          gap: 12, flexWrap: 'wrap',
        }}
      >
        <div className="filters" style={{ display: 'flex', gap: 6 }}>
          {(['activos', 'todos', 'inactivos'] as const).map((f) => (
            <button
              key={f}
              className={'chip' + (filtroActivo === f ? ' active' : '')}
              onClick={() => setFiltroActivo(f)}
              style={{ textTransform: 'capitalize' }}
            >
              {f}
              <span style={{ opacity: 0.7, marginLeft: 4 }}>
                ·{' '}
                {f === 'todos'
                  ? stats.total
                  : f === 'activos'
                    ? stats.activos
                    : stats.total - stats.activos}
              </span>
            </button>
          ))}
        </div>
        <div className="search-input-wrap" style={{ flex: '0 1 360px', minWidth: 200 }}>
          <MagnifyingGlass size={14} weight="bold" className="search-icon" />
          <input
            placeholder="Buscar por nombre, NIT, email o banco…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {/* Empty / Lista */}
      {loading ? (
        <div style={{ padding: 32, color: 'var(--muted-foreground)', fontSize: 13 }}>Cargando…</div>
      ) : filtered.length === 0 ? (
        rows.length === 0 ? (
          <EmptyState onCreate={() => setCreating(true)} />
        ) : (
          <div
            style={{
              padding: '40px 24px', background: 'var(--surface)',
              border: '1px dashed var(--border)', borderRadius: 12,
              textAlign: 'center', color: 'var(--muted-foreground)',
            }}
          >
            <p style={{ margin: 0, fontSize: 13 }}>
              Sin proveedores que coincidan con "<strong>{search}</strong>"
            </p>
          </div>
        )
      ) : (
        <div className="card-table">
          <table className="data-table">
            <thead>
              <tr>
                <th>Proveedor</th>
                <th>Contacto</th>
                <th>Régimen tributario</th>
                <th>Banco</th>
                <th style={{ textAlign: 'right' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <ProveedorRow key={p.id} proveedor={p} onEdit={() => setEditing(p)} onToggle={reload} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(creating || editing !== null) && (
        <ProveedorModal
          open
          proveedor={editing}
          onClose={() => { setCreating(false); setEditing(null) }}
          onSuccess={() => { setCreating(false); setEditing(null); reload() }}
        />
      )}
    </div>
  )
}

// -----------------------------------------------------------------------------
// Row de proveedor
// -----------------------------------------------------------------------------

function ProveedorRow({
  proveedor,
  onEdit,
  onToggle,
}: {
  proveedor: Proveedor
  onEdit: () => void
  onToggle: () => void
}) {
  const regimen = proveedor.regimen_tributario
    ? REGIMENES[proveedor.regimen_tributario]
    : REGIMENES.OTROS

  const handleToggle = async () => {
    const accion = proveedor.activo ? 'desactivar' : 'reactivar'
    if (!confirm(`¿${accion[0].toUpperCase() + accion.slice(1)} a ${proveedor.nombre}?`)) return
    const { error } = await supabase
      .from('proveedor')
      .update({ activo: !proveedor.activo, updated_at: new Date().toISOString() })
      .eq('id', proveedor.id)
    if (error) alert(error.message)
    else onToggle()
  }

  return (
    <tr style={{ opacity: proveedor.activo ? 1 : 0.55 }}>
      <td>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 32, height: 32, borderRadius: 8,
              background: 'var(--n-100)', color: 'var(--n-600)',
              display: 'grid', placeItems: 'center', flexShrink: 0,
            }}
          >
            <Buildings size={14} weight="duotone" />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600 }}>{proveedor.nombre}</div>
            <div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>
              {proveedor.nit ? `NIT ${proveedor.nit}` : 'Sin NIT'}
            </div>
          </div>
        </div>
      </td>
      <td>
        <div style={{ display: 'grid', gap: 2, fontSize: 12 }}>
          {proveedor.email && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--ink-soft)' }}>
              <EnvelopeSimple size={11} /> {proveedor.email}
            </span>
          )}
          {proveedor.telefono && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--ink-soft)' }}>
              <Phone size={11} /> {proveedor.telefono}
            </span>
          )}
          {!proveedor.email && !proveedor.telefono && (
            <span style={{ color: 'var(--muted-foreground)' }}>—</span>
          )}
        </div>
      </td>
      <td>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '4px 10px', borderRadius: 999,
          background: regimen.bg, color: regimen.color,
          fontSize: 11, fontWeight: 600,
        }}>
          {regimen.retiene ? <CheckCircle size={12} weight="fill" /> : <XCircle size={12} weight="fill" />}
          {regimen.label}
        </div>
        <div style={{ fontSize: 10, color: 'var(--muted-foreground)', marginTop: 3 }}>{regimen.sub}</div>
      </td>
      <td style={{ fontSize: 12 }}>
        {proveedor.banco ? (
          <div>
            <div style={{ fontWeight: 600 }}>{proveedor.banco}</div>
            {proveedor.cuenta_bancaria && (
              <div style={{ color: 'var(--muted-foreground)', fontVariantNumeric: 'tabular-nums' }}>{proveedor.cuenta_bancaria}</div>
            )}
          </div>
        ) : (
          <span style={{ color: 'var(--muted-foreground)' }}>—</span>
        )}
      </td>
      <td style={{ textAlign: 'right' }}>
        <div style={{ display: 'inline-flex', gap: 6 }}>
          <button className="btn btn-ghost btn-sm" onClick={onEdit} title="Editar">
            <PencilSimple size={13} />
          </button>
          <button className="btn btn-ghost btn-sm" onClick={handleToggle} title={proveedor.activo ? 'Desactivar' : 'Reactivar'}>
            {proveedor.activo ? <EyeSlash size={13} /> : <Eye size={13} />}
          </button>
        </div>
      </td>
    </tr>
  )
}

// -----------------------------------------------------------------------------
// Empty state
// -----------------------------------------------------------------------------

function EmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <div
      style={{
        padding: '60px 24px', textAlign: 'center',
        background: 'var(--surface)', border: '1px dashed var(--border)',
        borderRadius: 12,
      }}
    >
      <Buildings size={40} weight="duotone" color="var(--n-400)" />
      <h3 style={{ fontSize: 15, fontWeight: 600, margin: '14px 0 6px', color: 'var(--ink)' }}>
        Aún no hay proveedores
      </h3>
      <p style={{ fontSize: 13, color: 'var(--muted-foreground)', margin: '0 0 16px' }}>
        Registrá a los proveedores recurrentes (contador, arrendador, servicios) para que el<br />
        módulo de Gastos detecte automáticamente si retiene ISR o no.
      </p>
      <button className="btn btn-primary" onClick={onCreate}>
        <Plus size={14} weight="bold" />
        Registrar primer proveedor
      </button>
    </div>
  )
}

// -----------------------------------------------------------------------------
// Modal crear / editar
// -----------------------------------------------------------------------------

function ProveedorModal({
  open,
  proveedor,
  onClose,
  onSuccess,
}: {
  open: boolean
  proveedor: Proveedor | null
  onClose: () => void
  onSuccess: () => void
}) {
  const editing = proveedor !== null
  const [nombre, setNombre] = useState(proveedor?.nombre ?? '')
  const [nit, setNit] = useState(proveedor?.nit ?? '')
  const [regimen, setRegimen] = useState<Regimen>(proveedor?.regimen_tributario ?? 'OTROS')
  const [email, setEmail] = useState(proveedor?.email ?? '')
  const [telefono, setTelefono] = useState(proveedor?.telefono ?? '')
  const [banco, setBanco] = useState(proveedor?.banco ?? '')
  const [cuentaBancaria, setCuentaBancaria] = useState(proveedor?.cuenta_bancaria ?? '')
  const [direccion, setDireccion] = useState(proveedor?.direccion ?? '')
  const [notas, setNotas] = useState(proveedor?.notas ?? '')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!nombre.trim()) { setError('El nombre es obligatorio.'); return }
    setSubmitting(true)

    const payload = {
      nombre: nombre.trim(),
      nit: nit.trim() || null,
      regimen_tributario: regimen,
      email: email.trim() || null,
      telefono: telefono.trim() || null,
      banco: banco.trim() || null,
      cuenta_bancaria: cuentaBancaria.trim() || null,
      direccion: direccion.trim() || null,
      notas: notas.trim() || null,
      activo: true,
      updated_at: new Date().toISOString(),
    }

    const { error } = editing
      ? await supabase.from('proveedor').update(payload).eq('id', proveedor!.id)
      : await supabase.from('proveedor').insert(payload)

    setSubmitting(false)
    if (error) { setError(error.message); return }
    onSuccess()
  }

  const sel = REGIMENES[regimen]

  return (
    <Modal open={open} onClose={onClose} title={editing ? 'Editar proveedor' : 'Nuevo proveedor'} size="lg">
      <form onSubmit={submit} style={{ display: 'grid', gap: 14 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 12 }}>
          <Field label="Nombre o razón social" Icon={Buildings} required>
            <input
              autoFocus
              placeholder="Ej. Carlos Estuardo Pérez · Honorarios contables"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              required
            />
          </Field>
          <Field label="NIT" hint="Si no tiene NIT, dejar vacío">
            <input
              placeholder="12345678"
              value={nit}
              onChange={(e) => setNit(e.target.value)}
              style={{ fontVariantNumeric: 'tabular-nums' }}
            />
          </Field>
        </div>

        {/* Régimen tributario — el dato CLAVE */}
        <Field
          label="Régimen tributario"
          hint='Determina si el sistema retiene ISR automáticamente al cargar facturas de este proveedor.'
        >
          <select value={regimen} onChange={(e) => setRegimen(e.target.value as Regimen)}>
            {(Object.keys(REGIMENES) as Regimen[]).map((r) => (
              <option key={r} value={r}>{REGIMENES[r].label}</option>
            ))}
          </select>
          <div style={{
            marginTop: 8, padding: '8px 12px',
            background: sel.bg, color: sel.color,
            borderRadius: 6, fontSize: 11, fontWeight: 600,
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}>
            {sel.retiene ? <Warning size={13} weight="fill" /> : <CheckCircle size={13} weight="fill" />}
            {sel.retiene ? 'Al cargar facturas → se retiene ISR 5%/7%' : 'Al cargar facturas → NO se retiene'}
          </div>
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Email" Icon={EnvelopeSimple}>
            <input type="email" placeholder="contador@ejemplo.com" value={email} onChange={(e) => setEmail(e.target.value)} />
          </Field>
          <Field label="Teléfono" Icon={Phone}>
            <input placeholder="+502 5555 5555" value={telefono} onChange={(e) => setTelefono(e.target.value)} />
          </Field>
        </div>

        <Field label="Dirección" Icon={MapPin}>
          <input
            placeholder="Opcional · útil para constancias"
            value={direccion}
            onChange={(e) => setDireccion(e.target.value)}
          />
        </Field>

        {/* Banco para transferencias */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Banco para transferencias" Icon={Bank}>
            <input placeholder="BAC, Internacional, BI…" value={banco} onChange={(e) => setBanco(e.target.value)} />
          </Field>
          <Field label="Número de cuenta">
            <input
              placeholder="123456789"
              value={cuentaBancaria}
              onChange={(e) => setCuentaBancaria(e.target.value)}
              style={{ fontVariantNumeric: 'tabular-nums' }}
            />
          </Field>
        </div>

        <Field label="Notas internas" hint="Opcional. No se muestra al proveedor.">
          <textarea
            rows={2}
            placeholder="Ej. Factura mensual · Vence día 5 de cada mes"
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            style={{ resize: 'vertical' }}
          />
        </Field>

        {error && <div className="error-banner" style={{ fontSize: 13 }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={submitting}>
            Cancelar
          </button>
          <button type="submit" className="btn btn-primary" disabled={submitting || !nombre.trim()}>
            {submitting ? 'Guardando…' : editing ? 'Guardar cambios' : 'Crear proveedor'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

function Field({
  label, hint, Icon, required, children,
}: {
  label: string
  hint?: string
  Icon?: PhosphorIcon
  required?: boolean
  children: React.ReactNode
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 600, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {Icon && <Icon size={12} weight="bold" />}
        {label}
        {required && <span style={{ color: 'var(--brand-600)' }}>·</span>}
      </span>
      {children}
      {hint && <span style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>{hint}</span>}
    </label>
  )
}
